import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { type AuthLogo, AuthShellView, AuthStatusLine } from '../auth-shell/auth-shell.view'

/** The copy of {@link LoginView}. */
export interface LoginLabels {
	title: string
	subtitle: string
	action: string
	redirectHint: string
	loading: string
}

const DEFAULT_LABELS: LoginLabels = {
	title: 'Sign in',
	subtitle: 'Access your marketing workspace',
	action: 'Continue',
	redirectHint: "You'll be redirected to the secure sign-in page.",
	loading: 'Loading…',
}

/**
 * Props for {@link LoginView}.
 *
 * @property labels - The copy; any omitted label falls back to English.
 * @property logo - The bound brand's logo.
 * @property isLoading - True while auth state resolves: shows a status instead of the action.
 * @property onSignIn - Starts the hosted sign-in.
 */
export interface LoginViewProps {
	labels?: Partial<LoginLabels>
	logo?: AuthLogo
	isLoading?: boolean
	onSignIn: () => void
}

/**
 * The generic `/login` card (TO-361 login-surface REQ-001): one action that
 * starts the hosted sign-in, never a credential input. Pure.
 */
export function LoginView({ labels, logo, isLoading = false, onSignIn }: LoginViewProps): ReactElement {
	const copy = { ...DEFAULT_LABELS, ...labels }
	return (
		<AuthShellView
			logo={logo}
			title={copy.title}
			subtitle={copy.subtitle}
			footer={isLoading ? undefined : copy.redirectHint}
		>
			{isLoading ? (
				<AuthStatusLine text={copy.loading} />
			) : (
				<Button variant="primary" size="lg" className="w-full" onPress={onSignIn}>
					{copy.action}
				</Button>
			)}
		</AuthShellView>
	)
}
